import React from 'react';
import { Helmet } from 'react-helmet-async';
import { processFootnotes, getTranslatedType } from './NewsListHelper';
import PlayButton from "src/components/utils/PlayButton";

interface BaseStoryVideoProps {
  embedHtml: string;
  keyId: string;
  img_urls: string[];
  handleNewsClick: (key: string) => void;
}

// ⏩ Responsive <picture> wrapper
const ResponsivePicture: React.FC<{ url: string; alt: string; eager?: boolean }> = ({ url, alt, eager = false }) => {
  // Helper to append ?name= only if url !== '/favicon.ico'
  const withName = (size: string) => (url !== '/favicon.ico' ? `${url}?name=${size}` : url);

  return (
    <picture>
      <source media="(min-width: 1024px)" srcSet={withName('large')} />
      <source media="(min-width: 768px)" srcSet={withName('medium')} />
      <img
        src={withName('small')}
        alt={alt}
        className="w-full h-full object-cover"
        loading={eager ? 'eager' : 'lazy'}
        fetchPriority={eager ? 'high' : undefined}
        onError={(e) => { e.currentTarget.src = '/favicon.ico'; }}
      />
    </picture>
  );
};


// 🎞 Secondary Story Video
export const SecondaryStoryVideo: React.FC<BaseStoryVideoProps> = ({
  embedHtml,
  keyId,
  img_urls,
  handleNewsClick,
}) => {
  const baseUrl = img_urls.length > 0 ? img_urls[0] : '/favicon.ico';

  return (
    <div className="relative w-full aspect-[16/9] overflow-hidden">
      <ResponsivePicture url={baseUrl} alt="Video Thumbnail" eager={false} />

      {embedHtml && (
        <div className="absolute inset-0 flex items-center justify-center z-20">
          <PlayButton onClick={() => handleNewsClick(keyId)} />
        </div>
      )}

      <div
        className="absolute inset-0 w-full h-full z-10 cursor-pointer"
        onClick={() => handleNewsClick(keyId)}
      />
    </div>
  );
};

// 🎬 Main Story Video
interface MainStoryVideoProps extends BaseStoryVideoProps {
  title: string;
  postTime: number;
  type: string;
  subtype: string;
  t: any;
  language: string;
  getUrlLanguagePrefix: (lang: string) => string;
  navigate: (path: string) => void;
}

export const MainStoryVideo: React.FC<MainStoryVideoProps> = ({
  embedHtml,
  keyId,
  title,
  postTime,
  img_urls,
  type,
  subtype,
  t,
  language,
  getUrlLanguagePrefix,
  handleNewsClick,
  navigate,
}) => {
  const baseUrl = img_urls.length > 0 ? img_urls[0] : '/favicon.ico';
  const preloadUrl = `${baseUrl}?name=large`;

  const renderTypeLink = (type: string, subtype?: string) => {
    const label = getTranslatedType(subtype || type, t);
    const path = subtype ? `/${type}/${subtype}` : `/${type}`;
    const fullPath = `${getUrlLanguagePrefix(language)}${path}`;

    return (
      <span
        className="cursor-pointer text-blue-600 hover:text-blue-800 font-medium hover:underline inline-flex items-center"
        onClick={(e) => {
          e.stopPropagation();
          e.preventDefault();
          navigate(fullPath);
        }}
      >
        {label}
        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 ml-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
        </svg>
      </span>
    );
  };

  return (
    <>
      <Helmet>
        <link
          rel="preload"
          as="image"
          href={preloadUrl}
          imageSrcSet={`${baseUrl}?name=large 1024w, ${baseUrl}?name=medium 768w, ${baseUrl}?name=small 480w`}
          imageSizes="(min-width: 1024px) 100vw, (min-width: 768px) 100vw, 100vw"
        />
      </Helmet>

      <div className="flex flex-col lg:flex-row mt-1">
        {/* Mobile Title */}
        <div className="lg:hidden p-4">
          <h2
            onClick={() => handleNewsClick(keyId)}
            className="text-2xl font-extrabold text-blue-900 cursor-pointer hover:text-blue-950 transition-colors line-clamp-3 font-serif tracking-tight leading-tight"
          >
            {processFootnotes(title)}
          </h2>
          <p className="text-gray-600 text-sm mt-2">
            {new Date(postTime * 1000).toLocaleDateString()}
            <span className="mx-2">•</span>
            {type && renderTypeLink(type)}
            {type && subtype && ' > '}
            {subtype && renderTypeLink(type, subtype)}
          </p>
        </div>

        {/* Media */}
        <div className="relative w-full aspect-[16/9] overflow-hidden pr-5">
          <ResponsivePicture url={baseUrl} alt="Video Thumbnail" eager={true} />

          {embedHtml && (
            <div className="absolute inset-0 flex items-center justify-center z-20">
              <PlayButton onClick={() => handleNewsClick(keyId)} />
            </div>
          )}

          <div
            className="absolute inset-0 w-full h-full z-10 cursor-pointer"
            onClick={() => handleNewsClick(keyId)}
          />
        </div>

        {/* Desktop Title */}
        <div className="hidden lg:block w-6/13 mt-16 flex">
          <div className="px-2 self-center">
            <h2
              onClick={() => handleNewsClick(keyId)}
              className="text-3xl font-extrabold text-blue-900 cursor-pointer hover:text-blue-950 transition-colors text-left line-clamp-5 font-serif tracking-tight leading-tight"
            >
              {processFootnotes(title)}
            </h2>
            <p className="text-gray-600 text-sm mt-2 text-left">
              {new Date(postTime * 1000).toLocaleDateString()}
              <span className="mx-2">•</span>
              {type && renderTypeLink(type)}
              {type && subtype && ' > '}
              {subtype && renderTypeLink(type, subtype)}
            </p>
          </div>
        </div>
      </div>
    </>
  );
};
